import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SquaresFour, Planet, Wallet, ArrowLineDown, Sparkle, X, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

const KEY = "qs_onboarded";

const STEPS = [
  { route: "/dashboard", icon: SquaresFour, title: "Your Command Center", body: "This is your Overview — wallet balances, portfolio performance, allocation and recent activity, all derived live from your immutable ledger." },
  { route: "/dashboard/quantum-verse", icon: Planet, title: "Choose a Portfolio", body: "In Quantum Verse you select a portfolio, set the amount, and choose how payouts route. Everything is confirmed before anything is committed." },
  { route: "/dashboard", icon: Wallet, title: "Three Wallets, Clear Routing", body: "Earnings flow into three wallets — Main, Staking and Referral. Daily payouts route to Main; staking to Staking; referral commissions to Referral." },
  { route: "/dashboard/withdrawals", icon: ArrowLineDown, title: "Withdraw Anytime", body: "Request withdrawals in USDT or USDC over ERC-20 or TRC-20. First withdrawals complete a quick identity check, and every state is shown transparently." },
  { route: "/dashboard", icon: Sparkle, title: "You're all set", body: "Explore Quantum Flex, Referrals and Resources whenever you're ready. You can revisit this tour anytime from Account Profile." },
];

export function OnboardingTour() {
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem(KEY)) {
      const t = setTimeout(() => setActive(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const onReplay = () => { setI(0); setActive(true); navigate("/dashboard"); };
    window.addEventListener("qs:start-tour", onReplay);
    return () => window.removeEventListener("qs:start-tour", onReplay);
  }, [navigate]);

  const finish = () => { localStorage.setItem(KEY, "1"); setActive(false); };
  const go = (n) => {
    const clamped = Math.max(0, Math.min(STEPS.length - 1, n));
    setI(clamped);
    navigate(STEPS[clamped].route);
  };

  if (!active) return null;
  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center p-4 sm:p-6"
        data-testid="onboarding-tour"
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" onClick={finish} />
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full sm:max-w-md rounded-2xl border border-white/10 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-6 qs-glow-blue"
        >
          <button onClick={finish} data-testid="tour-skip" className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"><X size={18} /></button>
          <span className="grid place-items-center h-12 w-12 rounded-xl border border-[#0E9E90]/40 bg-[#0E9E90]/10 text-[#2DD4BF] mb-4">
            <step.icon size={24} weight="duotone" />
          </span>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#2DD4BF]">Step {i + 1} of {STEPS.length}</p>
          <h3 className="text-xl font-bold text-white mt-1">{step.title}</h3>
          <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{step.body}</p>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex gap-1.5">
              {STEPS.map((_, idx) => (
                <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-[#2DD4BF]" : "w-1.5 bg-white/15"}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {i > 0 && (
                <button onClick={() => go(i - 1)} data-testid="tour-back" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white text-sm hover:bg-white/5 transition-colors">
                  <ArrowLeft size={14} /> Back
                </button>
              )}
              {last ? (
                <button onClick={finish} data-testid="tour-finish" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#0E9E90] text-white text-sm font-medium hover:bg-[#1E4BCC] transition-colors">
                  Start exploring
                </button>
              ) : (
                <button onClick={() => go(i + 1)} data-testid="tour-next" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#0E9E90] text-white text-sm font-medium hover:bg-[#1E4BCC] transition-colors">
                  Next <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
